import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  deleteService,
  listService,
  saveService,
  serviceDetail,
} from "./callbacks";
import {
  IServicePayload,
  IDeleteDataResponseAndPayload,
  IServiceList,
} from "../../../types/api/service";

export const useServiceSave = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IServicePayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IServicePayload>,
    unknown,
    IServicePayload
  > = {},
): UseMutationResult<TResponse<IServicePayload>, unknown, IServicePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IServicePayload>, unknown, IServicePayload>({
    mutationFn: async (data: IServicePayload) => {
      const response = await saveService(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.SERVICE_LIST],
      });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};
export const useListService = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IServiceList[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IServiceList[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IServiceList[]>, Error>({
    queryKey: [API_ENDPOINTS.SERVICE_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listService(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};



export const useServiceDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {},
): UseMutationResult<
  TResponse<IDeleteDataResponseAndPayload>,
  unknown,
  IDeleteDataResponseAndPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  >({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const response = await deleteService(data);
      return response.data;
    },
    onSuccess: (res: TResponse<IDeleteDataResponseAndPayload>, ...rest) => {

      queryClient.setQueryData(
        [API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null }],
        (old: TResponse<IDeleteDataResponseAndPayload[]>) => {
          if (!old) return;
          return {
            ...old,
            data: Array.isArray(res.data.ids)
              ? old.data.filter((v: any) => ~res.data.ids.includes(v._id))
              : old.data.filter((v: any) => res.data.ids !== v._id),
          };
        },
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useServiceDetail = (
  slug: string,
  options?: Omit<
    UseQueryOptions<TResponse<IServicePayload>, Error>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery<TResponse<IServicePayload>, Error>({
    queryKey: [API_ENDPOINTS.SERVICE_DETAIL, slug],
    queryFn: async () => {
      const res = await serviceDetail(slug);
      return res.data;
    },
    enabled: !!slug,
    ...options,
  });
};
